<script lang="ts" setup>
  import { computed, ref, watch } from 'vue';
  import { InputWidthVariant } from '../InputWrapper';
  import { Radiobox } from '../Radiobox';
  import {
    horizontalRadioGroupClass,
    radioGroupLabelClass,
    radioGroupOptionLabelClass,
    verticalRadioGroupClass,
  } from './RadioBox.css';
  import {
    RadioGroupOrientation,
    type RadioGroupProps,
  } from './RadioGroup.types';

  const { options, orientation, label, defaultValue, name, modelValue } =
    defineProps<RadioGroupProps & { modelValue?: string }>();

  const state = ref<string>(
    modelValue ?? defaultValue ?? options[0]?.value ?? '',
  );

  const emits = defineEmits<{
    'update:modelValue': [value: string];
  }>();

  // Single watch to handle both internal state changes and external modelValue sync
  watch(
    [() => modelValue, state],
    ([newModelValue, newState], [oldModelValue, oldState]) => {
      // Sync state with external modelValue when it changes
      if (
        newModelValue !== undefined &&
        newModelValue !== oldModelValue &&
        newModelValue !== newState
      ) {
        state.value = newModelValue;
      }
      // Emit when state changes (from user interaction)
      else if (newState !== oldState) {
        emits('update:modelValue', newState);
      }
    },
  );

  const width = computed(() => {
    switch (orientation) {
      case RadioGroupOrientation.horizontal:
        return InputWidthVariant.MIN;
      case RadioGroupOrientation.vertical:
        return InputWidthVariant.FULL;
      default:
        return InputWidthVariant.FULL;
    }
  });

  const radioboxWrapperClass = computed(() => {
    switch (orientation) {
      case RadioGroupOrientation.horizontal:
        return horizontalRadioGroupClass;
      case RadioGroupOrientation.vertical:
        return verticalRadioGroupClass;
      default:
        return verticalRadioGroupClass;
    }
  });
</script>

<template>
  <div>
    <label for="radio-group" :class="radioGroupLabelClass">{{ label }}</label>
    <div :class="radioboxWrapperClass">
      <Radiobox
        v-for="item in options"
        :key="item.value"
        :name="name || 'radio-group'"
        :label="item.label"
        :value="item.value"
        v-model="state"
        :width="width"
      >
        <template #value>
          <slot :name="`option-${item.value}`" :item="item">
            <label
              :for="`${name || 'radio-group'}-${item.value}`"
              :class="radioGroupOptionLabelClass"
            >
              {{ item.label }}
            </label>
          </slot>
        </template>
      </Radiobox>
    </div>
  </div>
</template>
